import type { CloudProvider } from '@deepguide-ai/types'

export const CLOUD_TYPE_TO_SLUG: Record<string, CloudProvider> = {
  AWS_READONLY: 'aws',
  GCP_READONLY: 'gcp',
  AZURE_READONLY: 'azure',
  Cloudflare: 'cloudflare',
}

export function getCloudSlugForIntegrationType(
  integrationType: string | null | undefined
): CloudProvider | null {
  if (!integrationType) return null
  return CLOUD_TYPE_TO_SLUG[integrationType] ?? null
}

export function isCloudIntegrationType(
  integrationType: string | null | undefined
): boolean {
  return getCloudSlugForIntegrationType(integrationType) !== null
}

interface ConnectionCatalogEntry {
  id: string
  connectionProviderKey?: string | null
}

export function isIntegrationConnected(
  integration: ConnectionCatalogEntry,
  connectedProviderKeys: Set<string>,
  isGcpSccVerified: boolean
): boolean {
  if (integration.id === 'GCP_SECURITY_COMMAND_CENTER') {
    return isGcpSccVerified
  }
  return !!(
    integration.connectionProviderKey &&
    connectedProviderKeys.has(integration.connectionProviderKey)
  )
}

/**
 * Map an IntegrationType to the lowercase provider key the server's integration
 * health circuit breaker (`IntegrationHealth.provider`) uses. Most types
 * lowercase 1:1 (Okta→okta, Slack→slack); cloud types route through the slug
 * map, and a few multi-word / aliased types are spelled out. Used to match a
 * card to its breaker row for the "Auth error" badge — a miss just means no
 * badge, so an approximate map is safe.
 */
const HEALTH_PROVIDER_KEY_ALIASES: Record<string, string> = {
  GitHubIssues: 'github',
  GITHUB_ISSUES: 'github',
  GITHUB_SECURITY_ALERTS: 'github',
  GoogleWorkspace: 'google_workspace',
  BambooHR: 'bamboohr',
  Microsoft365: 'm365',
  M365: 'm365',
}

export function getHealthProviderKeyForIntegrationType(
  integrationType: string | null | undefined
): string | null {
  if (!integrationType) return null
  const cloudSlug = getCloudSlugForIntegrationType(integrationType)
  if (cloudSlug) return cloudSlug
  return HEALTH_PROVIDER_KEY_ALIASES[integrationType] ?? integrationType.toLowerCase()
}
